// Carry Roll BagSetup — club-specific, loft presets, estimate unmeasured (6.1, 6.2, 6.4).

import { useState } from 'react';
import { ExpressionInput, Select } from 'calc-controls';
import { useStore } from '../../store';
import { CLUB_PRESETS } from '@carry-roll/shared-logic';
import { estimateUnmeasured } from '@carry-roll/shared-logic';
import { distanceToM } from '@carry-roll/shared-logic';
import { ClubCategory, type BagEntry } from '@carry-roll/shared-types';
import { HelpTooltip } from '../calculator/HelpTooltip';

export function BagSetup() {
  const bag = useStore((s) => s.bag);
  const addBagEntry = useStore((s) => s.addBagEntry);
  const distanceUnit = useStore((s) => s.distanceUnit);
  const [clubName, setClubName] = useState('');
  const [carry, setCarry] = useState(0);
  const [presetIdx, setPresetIdx] = useState(0);

  const addEntry = () => {
    if (!clubName || carry <= 0) return;
    const c = distanceToM(carry, distanceUnit);
    if (!Number.isFinite(c)) return;
    const preset = CLUB_PRESETS[presetIdx];
    const entry: BagEntry = {
      clubName,
      category: preset?.category ?? ClubCategory.Iron,
      loftDeg: preset?.loftDeg ?? 30,
      carryM: c,
      totalM: c * 1.1,
      estimated: false,
    };
    addBagEntry(entry);
    setClubName('');
    setCarry(0);
  };

  const loadPresetBag = () => {
    const driver = bag.find((b) => b.category === ClubCategory.Driver);
    const driverCarry = driver?.carryM ?? 230;
    estimateUnmeasured(driverCarry).forEach((e) => addBagEntry(e));
  };

  return (
    <div data-testid="bag-setup">
      <h3 className="subsection-title">
        Bag setup
      </h3>
      <div className="flex gap-2 flex-wrap items-end">
        <label className="block">
          <span className="field-label">Club name</span>
          <input className="field-input" value={clubName} onChange={(e) => setClubName(e.target.value)} placeholder="7-iron" maxLength={40} autoComplete="off" />
        </label>
        <div className="block">
          <span className="field-label">
            Preset
            <HelpTooltip label="Club preset">Loft-based preset that sets the club category and default loft for this entry.</HelpTooltip>
          </span>
          <Select
            inline
            label="Preset"
            value={String(presetIdx)}
            onChange={(v) => setPresetIdx(Number(v))}
            options={CLUB_PRESETS.map((p, i) => ({ value: String(i), label: `${p.name} (${p.loftDeg}°)` }))}
          />
        </div>
        <div className="block">
          <span className="field-label">
            Carry ({distanceUnit})
            <HelpTooltip label="Club carry">Measured carry distance for this club.</HelpTooltip>
          </span>
          <ExpressionInput
            inline
            value={carry}
            onChange={setCarry}
            unit={distanceUnit}
            aria-label="Carry"
            decimals={0}
            min={0}
          />
        </div>
        <button type="button" className="btn btn-primary" onClick={addEntry}>
          Add
        </button>
        <button
          type="button"
          className="btn"
          onClick={loadPresetBag}
          title="Fills the bag with estimated clubs from your driver carry using standard loft spacing."
        >
          Estimate unmeasured
        </button>
      </div>
    </div>
  );
}
